import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Target, TrendingUp, Star, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const FitScore = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/documentation')} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Docs
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Home
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-8 h-8 text-green-600" />
            <h1 className="text-4xl font-bold text-slate-900">Understanding the Fit Score</h1>
          </div>
          <p className="text-lg text-slate-600">Learn how I-Match CRM calculates lead fit scores and how to use them effectively.</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>What is a Fit Score?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">
                The Fit Score is a numerical rating (0-100) that indicates how well a lead matches your ideal customer profile (ICP). It's calculated automatically using multiple data points including company size, industry, job title, location, and engagement signals.
              </p>
              <div className="flex gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Badge className="bg-red-100 text-red-700">0-30</Badge>
                  <span className="text-sm text-slate-600">Poor Fit</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-yellow-100 text-yellow-700">31-60</Badge>
                  <span className="text-sm text-slate-600">Moderate Fit</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700">61-100</Badge>
                  <span className="text-sm text-slate-600">Strong Fit</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Factors That Influence Fit Score
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold">1</div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Company Size</h3>
                    <p className="text-sm text-slate-600">Employee count and revenue data compared to your ICP</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold">2</div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Industry Match</h3>
                    <p className="text-sm text-slate-600">How closely the industry aligns with your target sectors</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold">3</div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Job Title Relevance</h3>
                    <p className="text-sm text-slate-600">Whether the contact holds a decision-making role</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold">4</div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Technology Stack</h3>
                    <p className="text-sm text-slate-600">Tools and platforms the company currently uses</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold">5</div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Engagement Signals</h3>
                    <p className="text-sm text-slate-600">Website visits, email opens, and other behavioral data</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-600" />
                How to Use Fit Scores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Sort and filter leads by fit score to prioritize high-value prospects</li>
                <li>Focus your outreach efforts on leads with scores above 60</li>
                <li>Use low-scoring leads for nurture campaigns rather than direct sales</li>
                <li>Combine fit score with engagement metrics for complete lead intelligence</li>
                <li>Regularly review and adjust your ICP settings to improve scoring accuracy</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                Important Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-700">
                Fit scores are automatically recalculated when:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li>A lead is enriched with new data</li>
                <li>Your ICP settings are updated</li>
                <li>New engagement data is recorded</li>
              </ul>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-slate-700">
                  <strong>Remember:</strong> Fit scores are predictive indicators, not guarantees. Always apply your own judgment and context when evaluating leads.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3 pt-6">
            <Button onClick={() => navigate('/documentation')} className="bg-indigo-600 hover:bg-indigo-700">
              Browse All Documentation
            </Button>
            <Button variant="outline" onClick={() => navigate('/contact')}>
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FitScore;
